'use client';

import { useEffect, useRef, type ReactNode } from 'react';
import { useQueryClient, type Query } from '@tanstack/react-query';
import { ApiProvider } from '@/contexts/ApiContext';
import { CentrifugoProvider } from '@/contexts/CentrifugoContext';
import { useAuthStore } from '@/stores/authStore';

const PUBLIC_KEY_HEADS = new Set([
  'shop',
  'cases',
  'draws',
  'realtime',
  'gameStats',
]);

const PUBLIC_TAILS: ReadonlyArray<readonly [string, string]> = [
  ['home', 'topDrops'],
  ['home', 'recentDrops'],
  ['home', 'topRecentDrops'],
  ['caseBattles', 'history'],
  ['caseBattles', 'count'],
];

function isPublicQuery(query: Query): boolean {
  const key = query.queryKey;
  if (!Array.isArray(key) || key.length === 0) return false;
  const head = key[0];
  if (typeof head !== 'string') return false;
  if (PUBLIC_KEY_HEADS.has(head)) return true;
  if (head === 'caseBattles') {
    if (key[1] === 'userActiveCount') return false;
    if (key[2] === 'mine') return false;
    return true;
  }
  for (const [h, t] of PUBLIC_TAILS) {
    if (head === h && key[1] === t) return true;
  }
  return false;
}

function isUserPrivateQuery(query: Query): boolean {
  return !isPublicQuery(query);
}

export function AuthedProviders({ children }: { children: ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const hydrated = useAuthStore((s) => s.hydrated);
  const queryClient = useQueryClient();

  const prevAuthRef = useRef(isAuthenticated);
  const baselineSetRef = useRef(false);
  useEffect(() => {
    if (!hydrated) return;
    if (!baselineSetRef.current) {
      baselineSetRef.current = true;
      prevAuthRef.current = isAuthenticated;
      return;
    }
    if (prevAuthRef.current === isAuthenticated) return;
    queryClient.removeQueries({ predicate: isUserPrivateQuery });
    prevAuthRef.current = isAuthenticated;
  }, [isAuthenticated, hydrated, queryClient]);

  return (
    <ApiProvider>
      <CentrifugoProvider anonymous={!isAuthenticated}>{children}</CentrifugoProvider>
    </ApiProvider>
  );
}
